import { z } from 'zod';

/** Team chat integration: Microsoft Teams (Workflows webhook) or Slack (incoming webhook). */
export const CHAT_PROVIDERS = [
  { key: 'teams', label: 'Microsoft Teams' },
  { key: 'slack', label: 'Slack' },
] as const;
export type ChatProvider = (typeof CHAT_PROVIDERS)[number]['key'];

/** Automatic posts an organization can turn on. */
export const CHAT_EVENTS = [
  { key: 'deal_created', label: 'New deal created' },
  { key: 'deal_won', label: 'Deal won' },
  { key: 'deal_lost', label: 'Deal lost' },
  { key: 'lead_created', label: 'New lead created' },
] as const;
export type ChatEvent = (typeof CHAT_EVENTS)[number]['key'];

/**
 * Webhook addresses the CRM may post to (anything else is refused, so the server can't be pointed at other sites).
 * Teams: a Workflows "When a Teams webhook request is received" trigger (Power Automate / Logic Apps hosts),
 * or a legacy *.webhook.office.com connector. Slack: hooks.slack.com.
 */
export const CHAT_WEBHOOK_RE: Record<ChatProvider, RegExp> = {
  teams: /^https:\/\/([a-z0-9-]+\.)+(logic\.azure\.com|powerplatform\.com|powerautomate\.com|webhook\.office\.com)(:443)?\/[^\s]{10,1500}$/i,
  slack: /^https:\/\/hooks\.slack\.com\/(services|triggers|workflows)\/[A-Za-z0-9/_-]{10,300}$/,
};

export const chatSettingsSchema = z.object({
  provider: z.enum(['teams', 'slack']),
  /** Leave out to keep the saved address (it is never sent back to the browser). */
  webhookUrl: z.string().trim().max(1600).optional(),
  channelName: z.string().trim().min(1, 'Enter the channel name').max(80),
  events: z.array(z.enum(CHAT_EVENTS.map((e) => e.key) as [ChatEvent, ...ChatEvent[]])).max(10).default([]),
}).strict().superRefine((v, ctx) => {
  if (v.webhookUrl && !CHAT_WEBHOOK_RE[v.provider].test(v.webhookUrl)) {
    ctx.addIssue({
      code: 'custom', path: ['webhookUrl'],
      message: v.provider === 'teams'
        ? 'Paste the address from the Teams workflow “When a Teams webhook request is received” (it starts with https:// and contains logic.azure.com or powerplatform.com).'
        : 'Paste the incoming-webhook address from Slack (it starts with https://hooks.slack.com/).',
    });
  }
});

export const chatShareSchema = z.object({ text: z.string().trim().max(1500).default('') }).strict();

export interface ChatStatus {
  connected: boolean;
  provider: ChatProvider | null;
  channelName: string | null;
  /** Last characters of the webhook address, for recognition only. */
  webhookHint: string | null;
  events: ChatEvent[];
}

export interface ChatPost {
  id: string;
  kind: 'share' | 'auto' | 'test';
  text: string;
  postedByName: string | null;
  ok: boolean;
  error: string | null;
  createdAt: string;
}
